import { GRASS_CHARACTERISTICS } from "@/data/grass-characteristics"
import { getRegionFromZipCode, REGIONAL_CLIMATE_DATA } from "@/data/regional-climate"
import { parseFrostDate } from "./date-utils"
import {
  attachToolEngineMetadata,
  buildCanonicalToolContext,
  type DeterministicToolResultMetadata,
} from "@/lib/lawn-plan-engine"

export interface AerationWindow {
  start: Date
  end: Date
  isPrimary: boolean
  description: string
}

export interface AerationResult extends DeterministicToolResultMetadata {
  grassType: string
  zipCode: string
  region: string
  currentSeason: 'spring' | 'summer' | 'fall' | 'winter'

  // Timing recommendations
  optimalSeasons: string[]
  recommendedSeason: 'spring' | 'fall' | 'now' | 'wait'
  timingAdvice: string

  // Specific timing windows
  springWindow: AerationWindow
  fallWindow: AerationWindow

  // Current status
  status: 'optimal' | 'acceptable' | 'wait' | 'approaching'
  isOptimalTime: boolean
  daysRemaining?: number
  timeUntilOptimal: string | null
  currentConditions: string

  // Guidelines
  soilTemperature: string
  frequency: string
  depth: string
  considerations: string[]

  // Regional data
  regionalData: {
    region: string
    lastFrost: Date | null
    firstFrost: Date | null
  }

  // Additional recommendations
  followUpActions: string[]
  avoidActions: string[]
}

export interface AerationInput {
  grassType: string
  zipCode: string
  lawnSize?: string | number | null
  sunlight?: string | null
}

export function calculateAerationTiming(
  input: AerationInput
): AerationResult {
  const { zipCode } = input
  const context = buildCanonicalToolContext({
    toolId: 'aeration',
    grassType: input.grassType,
    lawnSize: input.lawnSize,
    sunlight: input.sunlight,
  })
  const grass = GRASS_CHARACTERISTICS[context.grassType]
  if (!grass || !grass.aerationTiming) {
    throw new Error('Aeration data is not available for this grass type.')
  }

  const region = getRegionFromZipCode(zipCode)
  if (region === 'Unknown') {
    throw new Error('We could not determine your region from this ZIP code. Try a nearby ZIP.')
  }

  const climateData = REGIONAL_CLIMATE_DATA[region]
  if (!climateData) {
    throw new Error('Climate data is not available for your region.')
  }

  const now = new Date()
  const thisYear = now.getFullYear()
  const nextYear = thisYear + 1

  // Determine current season based on month
  const currentMonth = now.getMonth()
  let currentSeason: 'spring' | 'summer' | 'fall' | 'winter'
  if (currentMonth >= 2 && currentMonth <= 4) currentSeason = 'spring'
  else if (currentMonth >= 5 && currentMonth <= 7) currentSeason = 'summer'
  else if (currentMonth >= 8 && currentMonth <= 10) currentSeason = 'fall'
  else currentSeason = 'winter'

  // Build candidate windows for both this year and next year, then pick the
  // year that's currently relevant per window. Previously this used
  // `getCurrentGrowingYear()` which bumps to next year after September,
  // collapsing fall and spring onto the same year — so a Sept user saw the
  // CURRENT fall window dated for 2027 and was told "wait 353 days." Same bug
  // class as the herbicide picker.
  const isCoolSeason = grass.season === 'Cool'

  function buildWindowsForYear(yr: number): {
    springStart: Date
    springEnd: Date
    fallStart: Date
    fallEnd: Date
    lastFrost: Date | null
    firstFrost: Date | null
  } {
    const lf = climateData.lastFrost ? parseFrostDate(climateData.lastFrost, yr, false) : null
    const ff = climateData.firstFrost ? parseFrostDate(climateData.firstFrost, yr, true) : null

    const defaultSpringStart = new Date(yr, 3, 1) // April 1
    const defaultSpringEnd = new Date(yr, 4, 15) // May 15
    const defaultFallStart = new Date(yr, 8, 1) // September 1
    const defaultFallEnd = new Date(yr, 9, 30) // October 30

    let springStart: Date
    let springEnd: Date
    let fallStart: Date
    let fallEnd: Date

    if (isCoolSeason) {
      springStart = lf ? new Date(lf.getTime() + 7 * 86400000) : defaultSpringStart
      springEnd = lf ? new Date(lf.getTime() + 21 * 86400000) : defaultSpringEnd
      fallStart = ff ? new Date(ff.getTime() - 42 * 86400000) : defaultFallStart
      fallEnd = ff ? new Date(ff.getTime() - 21 * 86400000) : defaultFallEnd
    } else {
      // Warm-season: spring only — fall windows are placeholders
      springStart = lf ? new Date(lf.getTime() + 21 * 86400000) : defaultSpringStart
      springEnd = lf ? new Date(lf.getTime() + 42 * 86400000) : defaultSpringEnd
      fallStart = defaultFallStart
      fallEnd = defaultFallEnd
    }

    return { springStart, springEnd, fallStart, fallEnd, lastFrost: lf, firstFrost: ff }
  }

  const thisYr = buildWindowsForYear(thisYear)
  const nextYr = buildWindowsForYear(nextYear)

  // Pick the year for each window: this year if not yet past, else next year.
  const useThisYearSpring = now <= thisYr.springEnd
  const useThisYearFall = now <= thisYr.fallEnd

  const springWindowStart = useThisYearSpring ? thisYr.springStart : nextYr.springStart
  const springWindowEnd = useThisYearSpring ? thisYr.springEnd : nextYr.springEnd
  const fallWindowStart = useThisYearFall ? thisYr.fallStart : nextYr.fallStart
  const fallWindowEnd = useThisYearFall ? thisYr.fallEnd : nextYr.fallEnd

  // Use this year's frost dates for regionalData display (informational only).
  const lastFrost = thisYr.lastFrost
  const firstFrost = thisYr.firstFrost

  const springDescription = isCoolSeason
    ? 'Early spring after last frost'
    : 'Late spring after dormancy breaks'
  const fallDescription = isCoolSeason
    ? 'Early fall before first frost'
    : 'Not recommended for warm-season grasses'

  const springWindow: AerationWindow = {
    start: springWindowStart,
    end: springWindowEnd,
    isPrimary: !isCoolSeason, // Spring is primary for warm-season grasses
    description: springDescription
  }

  const fallWindow: AerationWindow = {
    start: fallWindowStart,
    end: fallWindowEnd,
    isPrimary: isCoolSeason, // Fall is primary for cool-season grasses
    description: fallDescription
  }

  // Determine recommended season and timing advice
  let recommendedSeason: 'spring' | 'fall' | 'now' | 'wait'
  let timingAdvice = ''
  let isOptimalTime = false
  let timeUntilOptimal: string | null = null
  let currentConditions = ''
  let status: 'optimal' | 'acceptable' | 'wait' | 'approaching' = 'wait'
  let daysRemaining: number | undefined

  // `now` is already defined above. Helpers for days until/from a date.
  const daysUntilDate = (date: Date) => Math.ceil((date.getTime() - now.getTime()) / (24 * 60 * 60 * 1000))

  if (isCoolSeason) {
    // Cool-season grasses prefer fall
    if (now >= fallWindow.start && now <= fallWindow.end) {
      recommendedSeason = 'now'
      timingAdvice = 'Perfect timing for fall aeration! This is the optimal window for cool-season grasses.'
      isOptimalTime = true
      currentConditions = 'Optimal fall aeration window'
      status = 'optimal'
      daysRemaining = daysUntilDate(fallWindow.end)
    } else if (now >= springWindow.start && now <= springWindow.end) {
      recommendedSeason = 'now'
      timingAdvice = 'Good timing for spring aeration. Fall is preferred, but spring works well too.'
      isOptimalTime = true
      currentConditions = 'Good spring aeration window'
      status = 'acceptable'
      daysRemaining = daysUntilDate(springWindow.end)
    } else if (now < fallWindow.start && daysUntilDate(fallWindow.start) <= 14) {
      recommendedSeason = 'wait'
      const daysUntil = daysUntilDate(fallWindow.start)
      timeUntilOptimal = `${daysUntil} days until optimal fall window`
      timingAdvice = 'The optimal fall aeration window is approaching.'
      currentConditions = 'Approaching fall window'
      status = 'approaching'
      daysRemaining = daysUntil
    } else if (now < springWindow.start && daysUntilDate(springWindow.start) <= 14) {
      recommendedSeason = 'wait'
      const daysUntil = daysUntilDate(springWindow.start)
      timeUntilOptimal = `${daysUntil} days until spring window`
      timingAdvice = 'The spring aeration window is approaching.'
      currentConditions = 'Approaching spring window'
      status = 'approaching'
      daysRemaining = daysUntil
    } else if (now < fallWindow.start) {
      recommendedSeason = 'wait'
      const daysUntil = daysUntilDate(fallWindow.start)
      timeUntilOptimal = `${daysUntil} days until optimal fall window`
      timingAdvice = 'Wait for the optimal fall aeration window to begin.'
      currentConditions = 'Waiting for fall window'
      status = 'wait'
    } else {
      recommendedSeason = 'spring'
      timingAdvice = 'Fall window has passed. Plan for spring aeration instead.'
      currentConditions = 'Fall window passed, plan for spring'
      status = 'wait'
    }
  } else {
    // Warm-season grasses: spring only
    if (now >= springWindow.start && now <= springWindow.end) {
      recommendedSeason = 'now'
      timingAdvice = 'Perfect timing for warm-season grass aeration! Spring is the only recommended season.'
      isOptimalTime = true
      currentConditions = 'Optimal spring aeration window'
      status = 'optimal'
      daysRemaining = daysUntilDate(springWindow.end)
    } else if (now < springWindow.start && daysUntilDate(springWindow.start) <= 14) {
      recommendedSeason = 'wait'
      const daysUntil = daysUntilDate(springWindow.start)
      timeUntilOptimal = `${daysUntil} days until optimal spring window`
      timingAdvice = 'The optimal spring aeration window is approaching.'
      currentConditions = 'Approaching spring window'
      status = 'approaching'
      daysRemaining = daysUntil
    } else if (now < springWindow.start) {
      recommendedSeason = 'wait'
      const daysUntil = daysUntilDate(springWindow.start)
      timeUntilOptimal = `${daysUntil} days until optimal spring window`
      timingAdvice = 'Wait for soil temperatures to warm up and grass to break dormancy.'
      currentConditions = 'Waiting for spring window'
      status = 'wait'
    } else {
      recommendedSeason = 'wait'
      timingAdvice = 'Spring window has passed. Wait for next spring for aeration.'
      currentConditions = 'Spring window passed, wait for next year'
      status = 'wait'
    }
  }

  // Follow-up actions and things to avoid
  const followUpActions = [
    'Water deeply after aeration to help soil settle',
    'Apply fertilizer within 2-3 weeks after aeration',
    'Consider overseeding for thin areas',
    'Avoid heavy foot traffic for 2-3 weeks',
    'Top-dress with compost if needed'
  ]

  const avoidActions = [
    'Aerating when soil is too wet or too dry',
    'Aerating during drought stress',
    'Aerating in winter (cool-season) or fall (warm-season)',
    'Heavy use of lawn immediately after aeration',
    'Skipping post-aeration watering'
  ]

  return attachToolEngineMetadata({
    grassType: grass.name,
    zipCode,
    region,
    currentSeason,
    optimalSeasons: grass.aerationTiming.optimalSeasons,
    recommendedSeason,
    timingAdvice,
    springWindow,
    fallWindow,
    status,
    isOptimalTime,
    daysRemaining,
    timeUntilOptimal,
    currentConditions,
    soilTemperature: grass.aerationTiming.soilTemperature,
    frequency: grass.aerationTiming.frequency,
    depth: grass.aerationTiming.depth,
    considerations: grass.aerationTiming.considerations,
    regionalData: {
      region,
      lastFrost,
      firstFrost
    },
    followUpActions,
    avoidActions
  }, context)
}
